import React, { useState } from 'react'
import { useTour } from '../../../contexts/TourContext'
import { useToast } from '../../../contexts/ToastContext'
import { IconZap, IconCheck, IconX, IconCopy, IconInfo } from '../../../components/Icons'

export default function OnboardingChecklist({ profile, userPvzs = [], employees = [], salaryConfig = {}, onNavigate }) {
  const { startTour } = useTour()
  const { showToast } = useToast()

  const [dismissed, setDismissed] = useState(() => {
    return localStorage.getItem('wb_onboarding_dismissed') === 'true'
  })

  // Рассчитываем выполнение шагов
  const isPvzConfigured = Array.isArray(userPvzs) && userPvzs.length > 0
  const isSalaryConfigured = Boolean(salaryConfig) && (
    Number(salaryConfig.dailyRate ?? salaryConfig.daily_rate ?? salaryConfig.rate_shift ?? 0) > 0 ||
    Number(salaryConfig.hourlyRate ?? salaryConfig.hourly_rate ?? salaryConfig.rate_hour ?? 0) > 0 ||
    Number(salaryConfig.bonusPerAccepted ?? salaryConfig.bonus_per_accepted ?? salaryConfig.bonus_acceptance ?? 0) > 0 ||
    Number(salaryConfig.bonusPerIssued ?? salaryConfig.bonus_per_issued ?? salaryConfig.bonus_issue ?? 0) > 0 ||
    Number(salaryConfig.turnoverPercent ?? salaryConfig.turnover_percent ?? 0) > 0
  )
  const isTeamInvited = Array.isArray(employees) && employees.filter(e => e.role !== 'owner').length > 0

  const completedStepsCount = (isPvzConfigured ? 1 : 0) + (isSalaryConfigured ? 1 : 0) + (isTeamInvited ? 1 : 0)
  const progressPercent = Math.round((completedStepsCount / 3) * 100)

  // Если всё выполнено на 100% или пользователь свернул — не показываем
  if (dismissed || progressPercent === 100) return null

  const handleDismiss = () => {
    setDismissed(true)
    localStorage.setItem('wb_onboarding_dismissed', 'true')
  }

  const handleCopyInvite = () => {
    const inviteCode = profile?.org?.invite_code || profile?.org_id || ''
    if (!inviteCode) {
      if (onNavigate) onNavigate('settings', { tab: 'profile' })
      return
    }
    const link = `${window.location.origin}/register?invite=${inviteCode}`
    navigator.clipboard.writeText(link)
      .then(() => showToast('Ссылка-приглашение скопирована!', 'success'))
      .catch(() => showToast('Не удалось скопировать', 'error'))
  }

  return (
    <div className="onboarding-checklist-card animate-fade-in">
      <div className="onboarding-checklist-header">
        <div className="onboarding-checklist-title-group">
          <span className="onboarding-checklist-badge">
            <IconZap size={13} style={{ marginRight: 4 }} />
            Быстрый старт
          </span>
          <h3 className="onboarding-checklist-title">Запуск вашего ПВЗ</h3>
        </div>
        <div className="onboarding-checklist-actions">
          <button 
            className="onboarding-btn-tour"
            onClick={() => startTour('general')}
            title="Запустить интерактивный гид по кнопкам"
          >
            <IconInfo size={14} style={{ marginRight: 5 }} />
            Интерактивный тур
          </button>
          <button 
            className="onboarding-btn-close"
            onClick={handleDismiss}
            title="Скрыть чек-лист"
          >
            <IconX size={16} />
          </button>
        </div>
      </div>

      <div className="onboarding-progress-bar-wrap">
        <div className="onboarding-progress-bar-fill" style={{ width: `${progressPercent}%` }}></div>
        <span className="onboarding-progress-text">Выполнено {completedStepsCount} из 3 ({progressPercent}%)</span>
      </div>

      <div className="onboarding-steps-grid">
        {/* Шаг 1 */}
        <div className={`onboarding-step-item ${isPvzConfigured ? 'is-completed' : ''}`}>
          <div className="onboarding-step-status">
            {isPvzConfigured ? <IconCheck size={14} /> : '1'}
          </div>
          <div className="onboarding-step-info">
            <span className="onboarding-step-name">Проверить ПВЗ</span>
            <span className="onboarding-step-desc">
              {isPvzConfigured ? 'ПВЗ успешно подключен' : 'Добавьте ваш первый ПВЗ'}
            </span>
          </div>
          {!isPvzConfigured && (
            <button 
              className="onboarding-step-btn"
              onClick={() => onNavigate && onNavigate('settings', { tab: 'general' })}
            >
              Настроить
            </button>
          )}
        </div>

        {/* Шаг 2 */}
        <div className={`onboarding-step-item ${isSalaryConfigured ? 'is-completed' : ''}`}>
          <div className="onboarding-step-status">
            {isSalaryConfigured ? <IconCheck size={14} /> : '2'}
          </div>
          <div className="onboarding-step-info">
            <span className="onboarding-step-name">Ставки и тарифы ЗП</span>
            <span className="onboarding-step-desc">
              {isSalaryConfigured ? 'Тарифы установлены' : 'Задайте оклад и бонусы'}
            </span>
          </div>
          {!isSalaryConfigured && (
            <button 
              className="onboarding-step-btn"
              onClick={() => onNavigate && onNavigate('settings', { tab: 'salary' })}
            >
              Задать ставки
            </button>
          )}
        </div>

        {/* Шаг 3 */}
        <div className={`onboarding-step-item ${isTeamInvited ? 'is-completed' : ''}`}>
          <div className="onboarding-step-status">
            {isTeamInvited ? <IconCheck size={14} /> : '3'}
          </div>
          <div className="onboarding-step-info">
            <span className="onboarding-step-name">Пригласить команду</span>
            <span className="onboarding-step-desc">
              {isTeamInvited ? 'Сотрудники в команде' : 'Отправьте инвайт-ссылку'}
            </span>
          </div>
          <button 
            className="onboarding-step-btn onboarding-step-btn--accent"
            onClick={handleCopyInvite}
          >
            <IconCopy size={13} style={{ marginRight: 4 }} />
            Ссылка-инвайт
          </button>
        </div>
      </div>

      <style>{`
        .onboarding-checklist-card {
          background: var(--surface-color, #ffffff);
          border: 1px solid var(--border-color, rgba(168, 85, 247, 0.25));
          border-radius: 16px;
          padding: 18px 20px;
          margin-bottom: 24px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
        }
        .onboarding-checklist-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 14px;
          flex-wrap: wrap;
          gap: 10px;
        }
        .onboarding-checklist-title-group {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .onboarding-checklist-badge {
          background: rgba(168, 85, 247, 0.12);
          color: #7c3aed;
          font-size: 11px;
          font-weight: 800;
          padding: 4px 10px;
          border-radius: 20px;
          border: 1px solid rgba(168, 85, 247, 0.25);
          display: inline-flex;
          align-items: center;
          text-transform: uppercase;
          letter-spacing: 0.4px;
        }
        .onboarding-checklist-title {
          font-size: 17px;
          font-weight: 800;
          color: var(--text-primary, #0f172a);
          margin: 0;
        }
        .onboarding-checklist-actions {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .onboarding-btn-tour {
          background: var(--wb-primary, #9333ea);
          color: #ffffff !important;
          border: none;
          padding: 7px 14px;
          border-radius: 8px;
          font-size: 12.5px;
          font-weight: 700;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          transition: all 0.2s ease;
          box-shadow: 0 2px 8px rgba(147, 51, 234, 0.25);
        }
        .onboarding-btn-tour:hover {
          opacity: 0.9;
          transform: translateY(-1px);
        }
        .onboarding-btn-close {
          background: transparent;
          border: none;
          color: var(--text-secondary, #64748b);
          cursor: pointer;
          padding: 6px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }
        .onboarding-btn-close:hover {
          background: var(--surface-hover, rgba(0, 0, 0, 0.05));
          color: var(--text-primary, #0f172a);
        }
        .onboarding-progress-bar-wrap {
          position: relative;
          height: 8px;
          background: var(--surface-hover, rgba(0, 0, 0, 0.06));
          border-radius: 4px;
          overflow: hidden;
          margin-bottom: 16px;
        }
        .onboarding-progress-bar-fill {
          height: 100%;
          background: linear-gradient(90deg, #9333ea, #c084fc);
          border-radius: 4px;
          transition: width 0.4s ease;
        }
        .onboarding-progress-text {
          position: absolute;
          right: 0;
          top: -20px;
          font-size: 11.5px;
          font-weight: 700;
          color: var(--text-secondary, #64748b);
        }
        .onboarding-steps-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 12px;
        }
        .onboarding-step-item {
          background: var(--surface-color, #f8fafc);
          border: 1px solid var(--border-color, #e2e8f0);
          border-radius: 12px;
          padding: 12px 14px;
          display: flex;
          align-items: center;
          gap: 12px;
          transition: all 0.2s ease;
        }
        .onboarding-step-item.is-completed {
          background: rgba(34, 197, 94, 0.06);
          border-color: rgba(34, 197, 94, 0.3);
        }
        .onboarding-step-status {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 13px;
          background: var(--surface-hover, #e2e8f0);
          color: var(--text-secondary, #64748b);
          flex-shrink: 0;
        }
        .onboarding-step-item.is-completed .onboarding-step-status {
          background: #22c55e;
          color: #ffffff;
        }
        .onboarding-step-info {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
        }
        .onboarding-step-name {
          font-size: 13.5px;
          font-weight: 700;
          color: var(--text-primary, #0f172a);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .onboarding-step-desc {
          font-size: 11.5px;
          color: var(--text-secondary, #64748b);
          margin-top: 2px;
        }
        .onboarding-step-btn {
          background: var(--wb-primary, #9333ea);
          border: none;
          color: #ffffff !important;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          white-space: nowrap;
          display: inline-flex;
          align-items: center;
          transition: all 0.2s ease;
        }
        .onboarding-step-btn:hover {
          opacity: 0.9;
        }
        .onboarding-step-btn--accent {
          background: #7c3aed;
          color: #ffffff !important;
        }
      `}</style>
    </div>
  )
}
